import {RootState} from 'store/store';
import {
    compPassBatch,
    passListAdapter,
} from 'pages/comp-pass/store/comp-pass-adapters';
import {selectSelectedFacility} from 'pages/facilities/store/facilities-selectors';
import {PASS_TYPE} from 'pages/comp-pass/components/comp-pass-wizard/utils';

/**
 * Selector to get the current step of comp pass creation.
 * @param state
 */
export const selectCompPassCreationStep = (state: RootState) =>
    state.compPass.passCreation.step;

/**
 * Selector to get the global error message for comp pass creation.
 * @param state
 */
export const selectCompPassGlobalErrors = (state: RootState) =>
    state.compPass.passCreation.errors.globalError;

/**
 * Selector to get the method used for adding comp pass recipients.
 * @param state
 */
export const selectCompPassCreationMethod = (state: RootState) =>
    state.compPass.passCreation.addRecipients.method;

/**
 * Selector to get the shared-link passes available per selected event/timeframe.
 * @param state
 */
export const selectSharedLinkAvailablePasses = (state: RootState) =>
    state.compPass.passCreation.addRecipients.sharedLink?.availablePasses ?? '';

/**
 * Selector to get the selected pass type (event vs. transient) for Step 1 of
 * the comp-pass creation wizard. `null` until the user (or auto-init) picks one.
 * @param state
 */
export const selectCompPassType = (state: RootState) =>
    state.compPass.passCreation.passType;

/**
 * Whether the selected facility can create transient comp passes (i.e. has the
 * transient capability). Drives the pass-type choice and the conditional nav /
 * table copy. Distinct from `selectCompPassType`, which is the runtime-selected
 * flow.
 * @param state
 */
export const selectIsTransientEnabled = (state: RootState) =>
    selectSelectedFacility(state).compPassesEnabledTypes.includes(
        PASS_TYPE.TRANSIENT
    );

/**
 * Whether the selected facility may issue extended-duration (up to 31-day)
 * transient comp passes. Drives the transient timeframe validation cap.
 * @param state
 */
export const selectExtendedCompPassesEnabled = (state: RootState) =>
    Boolean(selectSelectedFacility(state)?.extendedCompPassesEnabled);

/**
 * Selector to get the custom event offsets
 * ({enabled, eventStartsOffset, eventEndsOffset}) for Step 1 of the comp-pass
 * creation wizard. Offsets are `"HH:MM"` strings.
 * @param state
 */
export const selectEventOffsets = (state: RootState) =>
    state.compPass.passCreation.selectEvents.offsets;

/**
 * Selector to get all comp pass batches.
 * @param state
 */
export const {selectAll: selectAllCompPassBatches} = compPassBatch.getSelectors(
    (state: RootState) => state.compPass.passBatches.batches
);

export const selectCompPassBatchLinkDetails = (state: RootState) =>
    state.compPass.passBatchLinkDetails.data;

export const selectCompPassBatchLinkDetailsBatchId = (state: RootState) =>
    state.compPass.passBatchLinkDetails.batchId;

export const selectCompPassBatchLinkDetailsLoading = (state: RootState) =>
    state.compPass.passBatchLinkDetails.isLoading;

export const selectCompPassBatchLinkDetailsMeta = (state: RootState) =>
    state.compPass.passBatchLinkDetails.meta;

export const selectCompPassBatchLinkDetailsError = (state: RootState) =>
    state.compPass.passBatchLinkDetails.error;

/**
 * Selector to get the list of comp passes data.
 * @param state
 */
export const selectCompPassesData = passListAdapter.getSelectors(
    (state: RootState) => state.compPass.passListing.data
).selectAll;

/**
 * Selector to get the list of comp passes filters.
 * @param state
 */
export const selectCompPassListFilters = (state: RootState) =>
    state.compPass.passListing.filters;

/**
 * Selector to get the meta-data for comp passes.
 * @param state
 */
export const selectCompPassesMeta = (state: RootState) =>
    state.compPass.passListing.meta;

/**
 * Selector to get the loading state for comp passes.
 * @param state
 */
export const selectCompPassesLoading = (state: RootState) =>
    state.compPass.passListing.isLoading;

/**
 * Selector to get the redemption (entry/exit) rows for the usage-details flyout,
 * accumulated across infinite-scroll pages.
 * @param state
 */
export const selectCompPassUsageRows = (state: RootState) =>
    state.compPass.passUsage.rows;

/**
 * Selector to get the "received at" timestamp (`data.asOf`) for the usage-details
 * flyout. `null` before the first fetch resolves.
 * @param state
 */
export const selectCompPassUsageAsOf = (state: RootState) =>
    state.compPass.passUsage.asOf;

/**
 * Selector to get the pagination metadata for the usage-details flyout rows.
 * @param state
 */
export const selectCompPassUsageMeta = (state: RootState) =>
    state.compPass.passUsage.meta;

/**
 * Selector to get the loading state for the usage-details flyout.
 * @param state
 */
export const selectCompPassUsageLoading = (state: RootState) =>
    state.compPass.passUsage.isLoading;

/**
 * Selector for whether the last usage-details fetch failed (non-aborted).
 * @param state
 */
export const selectCompPassUsageError = (state: RootState) =>
    state.compPass.passUsage.isError;
